import { useMemo } from 'react';
import { useCompanyInstalledApps } from 'hooks/useCompanyInstalledApps';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useCompanyInstalledAppsStore } from 'stores/companyInstalledAppsStore';
import { resolveScopedCompanyId } from 'utils/allCompaniesMode';
import {
  resolveEmailNotificationSettingsVisibility,
  type EmailNotificationSettingsVisibility,
} from 'utils/emailNotificationSettingsVisibility';

export function useEmailNotificationSettingsVisibility(): {
  visibility: EmailNotificationSettingsVisibility;
  retry: () => Promise<void>;
} {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const companyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const installedAppsCompanyId = useCompanyInstalledAppsStore(
    (s) => s.companyId,
  );
  const { isAppInstalled, status, loadError, reload } =
    useCompanyInstalledApps();
  const { canAccessPath, isReady: permissionsReady } = useUserPermissions();

  const scopedCompanyId = resolveScopedCompanyId(activeCompanyId);

  const visibility = useMemo(
    () =>
      resolveEmailNotificationSettingsVisibility({
        companyInitialized,
        hasScopedCompany: scopedCompanyId !== null,
        permissionsReady,
        installedAppsStatus: status,
        installedAppsCompanyMatches:
          scopedCompanyId !== null &&
          installedAppsCompanyId === scopedCompanyId,
        loadError,
        isAppInstalled,
        canAccessPath,
      }),
    [
      canAccessPath,
      companyInitialized,
      installedAppsCompanyId,
      isAppInstalled,
      loadError,
      permissionsReady,
      scopedCompanyId,
      status,
    ],
  );

  return { visibility, retry: reload };
}
